import type { HintMarker, HintVerdict, NutriScoreGrade } from '@recipes/contracts';
import { cn } from '@recipes/ui';
import { Ban, StickyNote, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GRADES: NutriScoreGrade[] = ['A', 'B', 'C', 'D', 'E'];
// Official Nutri-Score colours; the letter is always there as well (never colour alone).
const GRADE_COLORS: Record<NutriScoreGrade, string> = {
  A: 'bg-[#038141] text-white',
  B: 'bg-[#85bb2f] text-black',
  C: 'bg-[#fecb02] text-black',
  D: 'bg-[#ee8100] text-black',
  E: 'bg-[#e63e11] text-white',
};

/**
 * The five-letter strip with the active letter enlarged (docs/finished/ingredient-catalog-spec.md
 * 6.10). `estimated` marks the app's own recipe estimate so it never looks like an awarded
 * label.
 */
export function NutriScoreBadge({
  grade,
  estimated = false,
  size = 'md',
  className,
}: {
  grade: NutriScoreGrade;
  estimated?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const { t } = useTranslation();
  const label = estimated
    ? t('catalog.nutriScore.estimatedLabel', { grade })
    : t('catalog.nutriScore.label', { grade });
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-testid="nutri-score"
      data-grade={grade}
      className={cn('inline-flex items-center gap-1 align-middle', className)}
    >
      <span className="inline-flex items-center overflow-hidden rounded-md border" aria-hidden>
        {GRADES.map((g) => (
          <span
            key={g}
            className={cn(
              'flex items-center justify-center font-bold leading-none',
              GRADE_COLORS[g],
              g === grade
                ? size === 'sm'
                  ? 'h-5 w-5 text-xs'
                  : 'h-7 w-7 text-base'
                : cn('opacity-40', size === 'sm' ? 'h-4 w-3 text-[9px]' : 'h-5 w-4 text-[10px]'),
            )}
          >
            {g}
          </span>
        ))}
      </span>
      {estimated && (
        <span className="text-xs text-muted-foreground" aria-hidden>
          {t('catalog.nutriScore.estimated')}
        </span>
      )}
    </span>
  );
}

const VERDICT_STYLE: Record<HintVerdict, string> = {
  AVOID: 'border-destructive/40 bg-destructive/10 text-destructive',
  RECOMMENDED: 'border-success/40 bg-success/10 text-success',
  NOTE: 'border-border bg-muted text-muted-foreground',
};
const VERDICT_ICON = { AVOID: Ban, RECOMMENDED: ThumbsUp, NOTE: StickyNote } as const;

/** Verdict as icon + text; colour only supports it (WCAG 2.2 AA). */
export function VerdictBadge({ verdict, className }: { verdict: HintVerdict; className?: string }) {
  const { t } = useTranslation();
  const Icon = VERDICT_ICON[verdict];
  return (
    <span
      data-testid="verdict-badge"
      data-verdict={verdict}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold',
        VERDICT_STYLE[verdict],
        className,
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {t(`catalog.verdict.${verdict}`)}
    </span>
  );
}

/** Marker on a list item or recipe row; renders nothing without an opinion. */
export function HintMarkerBadge({ marker, className }: { marker: HintMarker; className?: string }) {
  if (marker === 'NONE') return null;
  return <VerdictBadge verdict={marker} className={className} />;
}
